// 语音播放：播报员（报牌、牌局事实、开场白，排队播）与角色口头禅（催促、闲聊）。
// 读 public/voice/<角色id>/manifest.json（tools/voice_gen.mjs 生成），按事件随机挑一条播放；同一角色新的一句会打断上一句。
// 某个角色还没合成语音时安静（只显示文字气泡），不报错。
export class VoicePlayer {
  private manifests = new Map<string, Record<string, string[]> | null>();
  private playing = new Map<string, HTMLAudioElement>();
  enabled = true;
  volume = 0.9;

  /** 预先读取角色的语音清单（开局时调用，避免第一句延迟）。 */
  async preload(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => this.manifest(id)));
  }

  private async manifest(id: string): Promise<Record<string, string[]> | null> {
    if (this.manifests.has(id)) return this.manifests.get(id)!;
    let man: Record<string, string[]> | null = null;
    try {
      const res = await fetch(`voice/${id}/manifest.json`);
      if (res.ok) man = (await res.json()) as Record<string, string[]>;
    } catch { /* 没有语音：安静 */ }
    this.manifests.set(id, man);
    return man;
  }

  /** 有没有这条语音（用于决定要不要退回通用说法）。 */
  has(id: string, event: string): boolean {
    return !!this.manifests.get(id)?.[event]?.length;
  }

  /** 播一句；返回是否真的播了。 */
  async say(id: string, event: string): Promise<boolean> {
    if (!this.enabled) return false;
    const files = (await this.manifest(id))?.[event];
    if (!files?.length) return false;
    this.playing.get(id)?.pause();
    const a = new Audio(`voice/${id}/${files[Math.floor(Math.random() * files.length)]}`);
    a.volume = this.volume;
    this.playing.set(id, a);
    try { await a.play(); return true; } catch { return false; }
  }

  /** 有没有别的声音正在播（except 之外）。 */
  private busyOther(except: string): HTMLAudioElement | undefined {
    for (const [k, a] of this.playing) if (k !== except && !a.ended && !a.paused) return a;
    return undefined;
  }
  /** 等别人这一句说完（最多 3 秒），播报和口头禅不重叠。 */
  private afterOthers(except: string): Promise<void> {
    const a = this.busyOther(except);
    if (!a) return Promise.resolve();
    return new Promise((res) => { const done = () => res(); a.addEventListener('ended', done, { once: true }); a.addEventListener('pause', done, { once: true }); setTimeout(done, 3000); });
  }

  /** 播指定文件（文字气泡与语音要同一句：第 i 句台词对应 <事件>_<i>.mp3）。清单里没有就安静。角色口头禅等播报员这一句说完再说。 */
  async sayFile(id: string, file: string): Promise<boolean> {
    if (!this.enabled) return false;
    const man = await this.manifest(id);
    if (!man || !Object.values(man).some((fs) => fs.includes(file))) return false;
    await this.afterOthers(id);
    this.playing.get(id)?.pause();
    const a = new Audio(`voice/${id}/${file}`);
    a.volume = this.volume;
    this.playing.set(id, a);
    try { await a.play(); return true; } catch { return false; }
  }

  /**
   * 排队播（播报员用）：前一句没播完就排在后面，不打断；最多积压 2 句，再多就丢掉最旧的（跟不上牌局的播报没有意义）。
   */
  private waiting = new Map<string, string[]>();
  private starting = new Set<string>(); // 正在等别人说完、马上要播：这段时间来的新句子也要排队
  async queue(id: string, file: string): Promise<void> {
    if (!this.enabled) return;
    const man = await this.manifest(id);
    if (!man || !Object.values(man).some((fs) => fs.includes(file))) return;
    const q = this.waiting.get(id) ?? [];
    this.waiting.set(id, q);
    const cur = this.playing.get(id);
    if (this.starting.has(id) || (cur && !cur.ended && !cur.paused)) { q.push(file); if (q.length > 2) q.shift(); return; }
    void this.playNext(id, file);
  }
  private async playNext(id: string, file: string): Promise<void> {
    this.starting.add(id);
    await this.afterOthers(id); // 角色正在说口头禅时，播报等他说完
    this.starting.delete(id);
    if (!this.enabled) return;
    const a = new Audio(`voice/${id}/${file}`);
    a.volume = this.volume;
    this.playing.set(id, a);
    a.onended = () => { const next = this.waiting.get(id)?.shift(); if (next && this.enabled) void this.playNext(id, next); };
    void a.play().catch(() => { a.onended?.(new Event('ended')); });
  }

  /** 等所有声音（播报队列、口头禅）说完，最多等 maxMs 毫秒。用于一副结束时先让最后一手的播报说完再弹结算。 */
  idle(maxMs = 3500): Promise<void> {
    const t0 = performance.now();
    const busy = () => this.starting.size > 0 || [...this.waiting.values()].some((q) => q.length) || [...this.playing.values()].some((a) => !a.ended && !a.paused);
    return new Promise((res) => {
      const tick = () => (!busy() || performance.now() - t0 > maxMs ? res() : setTimeout(tick, 100));
      tick();
    });
  }

  stopAll(): void {
    this.waiting.clear();
    this.starting.clear();
    for (const a of this.playing.values()) a.pause();
    this.playing.clear();
  }
}
